import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useApp } from "@/contexts/AppContext";
import { userAccountService } from "@/services/userAccount.service";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const EXTERNAL_LINK_WARNING_STORAGE_KEY = "external-link-warning-disabled";

type PendingNavigation = {
  href: string;
  target: string | null;
};

const readLocalPreference = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(EXTERNAL_LINK_WARNING_STORAGE_KEY) === "1";
};

const persistLocalPreference = (disabled: boolean) => {
  if (typeof window === "undefined") return;
  if (disabled) {
    window.localStorage.setItem(EXTERNAL_LINK_WARNING_STORAGE_KEY, "1");
    return;
  }
  window.localStorage.removeItem(EXTERNAL_LINK_WARNING_STORAGE_KEY);
};

/**
 * Global guard that warns users before leaving WebKurator via external http(s) links.
 * Stores opt-out in localStorage for guests and in user_account for authenticated users.
 */
export function ExternalLinkWarningDialog() {
  const { t } = useApp();
  const { user, isAuthenticated, updateExternalLinkWarningDisabled } = useAuth();
  const [open, setOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [pendingNavigation, setPendingNavigation] =
    useState<PendingNavigation | null>(null);
  const [localPreferenceDisabled, setLocalPreferenceDisabled] = useState<boolean>(
    () => readLocalPreference()
  );

  const warningDisabled = useMemo(() => {
    const userDisabled = Boolean(user?.externalLinkWarningDisabled);
    if (isAuthenticated) {
      return userDisabled || localPreferenceDisabled;
    }
    return localPreferenceDisabled;
  }, [isAuthenticated, localPreferenceDisabled, user?.externalLinkWarningDisabled]);

  const userId = user?.id ?? null;

  useEffect(() => {
    if (!isAuthenticated || !userId) return;
    if (typeof user?.externalLinkWarningDisabled === "boolean") return;

    let cancelled = false;
    const fetchPreference = async () => {
      try {
        const response = await userAccountService.getExternalLinkWarningPreference();
        if (!cancelled) {
          updateExternalLinkWarningDisabled(response.disabled);
        }
      } catch (error) {
        console.error("Failed to load external-link warning preference", error);
      }
    };

    void fetchPreference();
    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    updateExternalLinkWarningDisabled,
    userId,
    user?.externalLinkWarningDisabled,
  ]);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || warningDisabled) {
        return;
      }

      const target = event.target as Element | null;
      const anchor = target?.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor || anchor.dataset.externalWarning === "off") {
        return;
      }

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) {
        return;
      }

      let targetUrl: URL;
      try {
        targetUrl = new URL(href, window.location.href);
      } catch {
        return;
      }

      const protocol = targetUrl.protocol.toLowerCase();
      if (protocol !== "http:" && protocol !== "https:") {
        return;
      }

      if (targetUrl.origin === window.location.origin) {
        return;
      }

      event.preventDefault();
      setDontShowAgain(false);
      setPendingNavigation({
        href: targetUrl.toString(),
        target: anchor.getAttribute("target"),
      });
      setOpen(true);
    };

    document.addEventListener("click", handleDocumentClick, true);
    return () => document.removeEventListener("click", handleDocumentClick, true);
  }, [warningDisabled]);

  const handleProceed = async () => {
    if (!pendingNavigation) {
      setOpen(false);
      return;
    }

    if (dontShowAgain) {
      persistLocalPreference(true);
      setLocalPreferenceDisabled(true);
      if (isAuthenticated && user) {
        try {
          const response =
            await userAccountService.updateExternalLinkWarningPreference({
              disabled: true,
            });
          updateExternalLinkWarningDisabled(response.disabled);
        } catch (error) {
          console.error("Failed to persist external-link warning preference", error);
        }
      }
    }

    const target = (pendingNavigation.target || "").toLowerCase();
    if (target && target !== "_self") {
      window.open(pendingNavigation.href, pendingNavigation.target || "_blank", "noopener,noreferrer");
    } else {
      window.location.assign(pendingNavigation.href);
    }

    setOpen(false);
    setPendingNavigation(null);
  };

  const handleCancel = () => {
    setOpen(false);
    setPendingNavigation(null);
    setDontShowAgain(false);
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => (!nextOpen ? handleCancel() : setOpen(true))}>
      <DialogContent className="dialog-content-standard">
        <DialogHeader>
          <DialogTitle>{t("common.externalLinkWarningTitle")}</DialogTitle>
          <DialogDescription>
            {t("common.externalLinkWarningBody")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2">
          <Checkbox
            id="external-link-warning-opt-out"
            checked={dontShowAgain}
            onCheckedChange={(checked) => setDontShowAgain(Boolean(checked))}
          />
          <Label htmlFor="external-link-warning-opt-out" className="text-sm">
            {t("common.externalLinkWarningDoNotShowAgain")}
          </Label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleProceed}>
            {t("common.externalLinkWarningProceed")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
